import type { FrameGeometry, ViewportRect } from "../coordinates/sourceToViewport";
import type { SyncFrameDescriptorPayload } from "../envelope";

/**
 * stageGeometry - pure helpers that turn DOM measurements and the room's server-authoritative
 * frame descriptor into the FrameGeometry/ViewportRect inputs sourceToViewport (05-04) needs
 * (D-05, D-07). No DOM access here: callers (SyncStageOverlay) do the measuring and pass in
 * plain strings and numbers, which keeps this module unit-testable without jsdom.
 */

const MATRIX_PATTERN = /^matrix(?:3d)?\(([^)]*)\)$/;

/**
 * Whether a CSS `transform` computed style mirrors its element horizontally - true when the
 * transform's first matrix component is negative (LiveKit's own `rotateY(180deg)` mirror rule
 * on the local front camera reports exactly this, see the plan's interfaces block).
 */
export function isMirroredTransform(css: string): boolean {
  const match = MATRIX_PATTERN.exec(css.trim());
  if (!match) {
    return false;
  }
  const first = Number.parseFloat(match[1].split(",")[0] ?? "");
  return Number.isFinite(first) && first < 0;
}

/** A rect with the same shape a DOMRect/getBoundingClientRect() result has. */
export type RectLike = { left: number; top: number; width: number; height: number };
/** Only the origin of an outer rect is needed to make `inner` relative to it. */
export type OriginLike = { left: number; top: number };

/** `inner`'s position made relative to `outer`'s origin - e.g. the stage video inside the overlay. */
export function relativeRect(inner: RectLike, outer: OriginLike): ViewportRect {
  return {
    x: inner.left - outer.left,
    y: inner.top - outer.top,
    width: inner.width,
    height: inner.height,
  };
}

/**
 * D-05: mirroring is a per-viewer DISPLAY property, never stored - only the owner of a mirrored
 * (front-camera) source sees it mirrored; every other viewer sees the canonical, unmirrored image.
 */
export function displayGeometry(frame: SyncFrameDescriptorPayload, viewerIsOwner: boolean): FrameGeometry {
  return {
    sourceW: frame.sourceW,
    sourceH: frame.sourceH,
    mirrored: frame.mirrored && viewerIsOwner,
    rotation: frame.rotation,
  };
}

/**
 * Geometry for a live (non-frozen) stage video. Rotation is always 0: browsers deliver already
 * upright frames for live WebRTC video (unlike a captured frame descriptor, which can carry a
 * non-zero rotation recorded at capture time). Returns null for a not-yet-loaded video (0 or
 * non-finite intrinsic size), so callers can render nothing interactive until metadata arrives.
 */
export function liveGeometry(videoWidth: number, videoHeight: number, mirrored: boolean): FrameGeometry | null {
  if (!Number.isFinite(videoWidth) || !Number.isFinite(videoHeight) || videoWidth <= 0 || videoHeight <= 0) {
    return null;
  }
  return { sourceW: videoWidth, sourceH: videoHeight, mirrored, rotation: 0 };
}
